/**
 * The top of the dashboard (migration 096; owner feedback 2026-09-28: "such
 * approval request should be in top of dashboard"). Owners and admins only.
 *
 *   - Needs your approval: a change an AI agent asked for that is HELD
 *     (server HELD_KINDS, today only inviting a teammate, and older changes
 *     from before 096): what it is, who asked (the agent token and the person
 *     it acts for), when, and when it would apply. Approve / Reject.
 *   - Made by an AI agent, live now: every change an agent made in the last
 *     7 days that can still be undone and nobody has kept yet, with what
 *     changed (before → after). Undo / Keep it.
 *
 * Nothing renders when there is nothing to decide. The same changes are
 * emailed to every owner and admin at once, with their own Undo or Approve /
 * Reject links (pages/AgentChangeLink.tsx). The agent's note is its own words
 * and is shown as a quote.
 */
import { useState } from 'react';
import type { PendingChange } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { SecretReveal } from '../screens/Destinations';
import { ErrorNotice, Modal, Time, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';
import { CONFIRM } from './PendingApprovals';

/** A plain-language name for each kind, for the banner's headings. */
export const KIND_WORDS: Record<PendingChange['kind'], string> = {
  'destination.create': 'New lead destination',
  'destination.update': 'Lead destination moved',
  'vendor.activate': 'Contractors switched on',
  'routing.escalation': 'Escalation contacts changed',
  'site.network_fallback': 'Network fallback switched on',
  'member.invite': 'Invite a teammate',
  'vendor.reconsent': 'Contractor opt-out lifted',
  'config.contact_targets': 'New phone number or link for homeowners',
};

/** Held: waiting for an owner or admin. */
export const isHeld = (c: PendingChange): boolean => c.status === 'pending';

/** Made by an agent, live, still undoable and not yet kept. */
export const isUndoable = (c: PendingChange): boolean =>
  c.status === 'applied' && Boolean(c.requested_by.agent_token_id) && Boolean(c.undo?.available) && !c.reviewed_at;

function Who({ c }: { c: PendingChange }) {
  return (
    <>
      the AI agent “{c.requested_by.agent_token_label ?? 'unknown'}”, acting for {c.requested_by.email ?? 'an unknown person'}
    </>
  );
}

function Changes({ c }: { c: PendingChange }) {
  if (!c.changes?.length) return null;
  return (
    <ul className="small agent-change-diff">
      {c.changes.slice(0, 8).map((d, i) => (
        <li key={`${d.field}-${i}`}>
          <strong>{d.field}:</strong> <span className="muted">{d.before}</span> → {d.after}
        </li>
      ))}
      {c.changes.length > 8 ? <li className="muted">…and {c.changes.length - 8} more</li> : null}
    </ul>
  );
}

export function AgentChangesBanner() {
  const { api, role } = useSession();
  const enabled = can(role, 'approveChanges');
  const toast = useToast();
  const list = useQuery(() => api.listPendingChanges(), [], { enabled });
  const [confirming, setConfirming] = useState<PendingChange | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const reload = () => void list.reload();
  const approve = useMutation(async (id: string) => {
    const res = await api.approvePendingChange(id);
    setConfirming(null);
    if (res.secret) setSecret(res.secret);
    else toast('Approved');
    reload();
    return res;
  });
  const reject = useMutation(async (id: string) => {
    await api.rejectPendingChange(id);
    toast('Rejected. Nothing was changed.');
    reload();
    return null;
  });
  const undo = useMutation(async (id: string) => {
    await api.undoPendingChange(id);
    toast('Undone');
    reload();
    return null;
  });
  const keep = useMutation(async (id: string) => {
    await api.keepPendingChange(id);
    reload();
    return null;
  });

  if (!enabled) return null;
  const items = list.data?.items ?? [];
  const held = items.filter(isHeld);
  const made = items.filter(isUndoable);
  // A failed list read hides the banner (the dashboard stays usable, and AI
  // agent access lists the same changes); a failed decision is shown.
  const error = approve.error ?? reject.error ?? undo.error ?? keep.error;
  if (!held.length && !made.length && !error) return null;

  return (
    <section className="stack agent-changes-banner" aria-labelledby="agent-changes-title" data-testid="agent-changes-banner">
      <h2 id="agent-changes-title" className="visually-hidden">
        AI agent changes
      </h2>
      <ErrorNotice error={error} />
      {held.length ? (
        <div className="notice warn stack-sm" role="region" aria-labelledby="agent-held-title">
          <h3 id="agent-held-title" style={{ margin: 0 }}>
            {held.length === 1 ? 'An AI agent is waiting for your approval' : `${held.length} AI agent requests are waiting for your approval`}
          </h3>
          {held.map((c) => (
            <article key={c.id} className="stack-sm pending-item" aria-label={KIND_WORDS[c.kind]}>
              <strong style={{ overflowWrap: 'anywhere' }}>
                {KIND_WORDS[c.kind]}: {c.summary}
              </strong>
              <span className="small">
                Asked by <Who c={c} /> · <Time iso={c.created_at} />
              </span>
              <span className="small">
                {c.applies_at ? (
                  <>
                    Takes effect <Time iso={c.applies_at} /> unless you reject it.
                  </>
                ) : (
                  <>
                    Nothing happens until an owner or admin approves it. If nobody does, the request expires <Time iso={c.expires_at} />.
                  </>
                )}
              </span>
              {c.note ? (
                <p className="small agent-quote" style={{ margin: 0 }}>
                  <span className="visually-hidden">The agent’s note: </span>“{c.note}”
                </p>
              ) : null}
              <div className="row">
                <button type="button" className="btn btn-sm btn-primary" onClick={() => setConfirming(c)}>
                  {c.applies_at ? 'Apply now' : 'Review and approve'}
                </button>
                <button type="button" className="btn btn-sm" disabled={reject.pending} onClick={() => void reject.run(c.id)}>
                  Reject
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : null}
      {made.length ? (
        <div className="notice info stack-sm" role="region" aria-labelledby="agent-made-title">
          <h3 id="agent-made-title" style={{ margin: 0 }}>
            {made.length === 1 ? 'An AI agent made a change. It is live now.' : `AI agents made ${made.length} changes. They are live now.`}
          </h3>
          <p className="small" style={{ margin: 0 }}>
            Owners and admins were emailed each one with an Undo link. If you did not want one, undo it here; otherwise keep it.
          </p>
          {made.map((c) => (
            <article key={c.id} className="stack-sm pending-item" aria-label={KIND_WORDS[c.kind]}>
              <strong style={{ overflowWrap: 'anywhere' }}>
                {KIND_WORDS[c.kind]}: {c.summary}
              </strong>
              <Changes c={c} />
              <span className="small">
                By <Who c={c} /> · <Time iso={c.decided_at ?? c.created_at} />
                {c.undo?.until ? (
                  <>
                    {' '}
                    · Undo works until <Time iso={c.undo.until} />
                  </>
                ) : null}
              </span>
              {c.note ? (
                <p className="small agent-quote" style={{ margin: 0 }}>
                  <span className="visually-hidden">The agent’s note: </span>“{c.note}”
                </p>
              ) : null}
              <div className="row">
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  disabled={undo.pending}
                  onClick={() => {
                    if (window.confirm(`Undo “${c.summary}”? ${c.undo?.how ? `This will ${c.undo.how.replace(/\.$/, '')}.` : ''}`)) void undo.run(c.id);
                  }}
                >
                  Undo
                </button>
                <button type="button" className="btn btn-sm" disabled={keep.pending} onClick={() => void keep.run(c.id)}>
                  Keep it
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : null}
      <Modal
        open={Boolean(confirming)}
        title={confirming ? CONFIRM[confirming.kind].title : ''}
        onClose={() => setConfirming(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setConfirming(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={approve.pending} onClick={() => confirming && void approve.run(confirming.id)}>
              {approve.pending ? 'Applying…' : confirming?.applies_at ? 'Apply now' : 'Approve'}
            </button>
          </>
        }
      >
        {confirming ? (
          <div className="stack-sm">
            <p>
              <strong style={{ overflowWrap: 'anywhere' }}>{confirming.summary}</strong>
            </p>
            <p>{CONFIRM[confirming.kind].body}</p>
          </div>
        ) : null}
      </Modal>
      <SecretReveal secret={secret} onDone={() => setSecret(null)} />
    </section>
  );
}
